import React, { Children, lazy, memo } from 'react';
import { motion } from 'framer-motion';
import { BonusCard } from './components/BonusCard';
import { Footer } from './components/Footer';
import { CheckIcon, Star, Sparkles } from 'lucide-react';
// Simplified animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function App() {
  return <div className="min-h-screen bg-[#F7F3EF] text-[#3A3A3A] font-sans selection:bg-[#E8CFCF] selection:text-[#3A3A3A]" data-id="element-0">
      {/* 1. HERO SECTION */}
      <header className="py-16 px-4 md:py-24 bg-white border-b border-[#E8CFCF]/40" data-id="element-1">
        <div className="max-w-6xl mx-auto" data-id="element-2">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="grid md:grid-cols-2 gap-12 md:gap-16 items-center" data-id="element-3">
            {/* LEFT COLUMN - Text */}
            <div className="order-2 md:order-1 space-y-6" data-id="element-4">
              <motion.div variants={fadeInUp} data-id="element-5">
                <span className="inline-flex items-center gap-2 py-2 px-5 rounded-full bg-[#F5EAEA] text-[#D88C7A] text-sm font-bold border border-[#E8CFCF]" data-id="element-6">
                  <Sparkles className="w-4 h-4" data-id="element-7" />
                  CRIANZA CON AMOR DE MAMÁ
                </span>
              </motion.div>

              <motion.h1 variants={fadeInUp} className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-[#3A3A3A] leading-[1.08] tracking-tight" data-id="element-8">
                QUÉ HACER EN UNA RABIETA{' '}
                <span className="text-[#D88C7A]" data-id="element-9">SIN GRITAR, SIN CULPA</span> Y
                SIN PERDER EL CONTROL
              </motion.h1>

              <motion.p variants={fadeInUp} className="text-lg md:text-xl text-[#3A3A3A]/80 leading-relaxed" data-id="element-10">
                La guía práctica + tarjetas imprimibles que te dicen exactamente
                qué hacer, qué decir y cómo reaccionar cuando tu hijo está
                desbordado.
              </motion.p>

              <motion.div variants={fadeInUp} className="space-y-3 py-2" data-id="element-11">
                {['Guía clara para actuar en el momento exacto', 'Frases listas para usar sin improvisar', 'Herramientas reales para manejar rabietas con más calma'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-12">
                    <CheckIcon className="w-5 h-5 text-[#D88C7A] flex-shrink-0 mt-0.5" data-id="element-13" />
                    <span className="text-[#3A3A3A]/80 font-medium" data-id="element-14">
                      {item}
                    </span>
                  </div>)}
              </motion.div>

              <motion.div variants={fadeInUp} className="pt-2 space-y-3" data-id="element-15">
                <a href="https://43skvt-1y.myshopify.com/products/guia-de-crianza-con-inteligencia-emocional" target="_blank" rel="noopener noreferrer" className="block w-full sm:w-auto sm:inline-block bg-[#D88C7A] hover:bg-[#C57665] text-white font-extrabold py-5 px-10 rounded-full shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all duration-300 text-center" data-id="element-16">
                  <span className="block text-2xl md:text-3xl mb-0.5" data-id="element-17">
                    $12.75 USD
                  </span>
                  <span className="block text-lg md:text-xl" data-id="element-18">
                    🔥 QUIERO DESCARGARLO AHORA
                  </span>
                </a>
                <div className="flex flex-wrap gap-4 text-sm text-[#3A3A3A]/60 font-semibold" data-id="element-19">
                  <span data-id="element-20">✔ Acceso inmediato</span>
                  <span data-id="element-21">✔ Descarga instantánea</span>
                  <span data-id="element-22">✔ Pago seguro</span>
                </div>
              </motion.div>
            </div>

            {/* RIGHT COLUMN - Image */}
            <motion.div variants={fadeInUp} className="order-1 md:order-2 flex justify-center" data-id="element-23">
              <img src="https://cdn.magicpatterns.com/uploads/gCBnCrd2GBvtoQ7qAhVGW8/ChatGPT_comprimir_2_(1).png" alt="Guía de Crianza con Inteligencia Emocional" className="w-full max-w-sm md:max-w-md lg:max-w-lg rounded-2xl shadow-2xl" loading="eager" fetchPriority="high" decoding="sync" width={600} height={630} data-id="element-24" />
            </motion.div>
          </motion.div>
        </div>
      </header>

      {/* BLOQUE PERSUASIVO */}
      <section className="py-10 px-4 bg-[#F7F3EF] border-b border-[#E8CFCF]/30" data-id="element-25">
        <div className="max-w-2xl mx-auto text-center space-y-4" data-id="element-26">
          <h2 className="text-2xl md:text-4xl font-extrabold text-[#3A3A3A] leading-tight" data-id="element-27">
            CUANDO PASE LA PRÓXIMA RABIETA…{' '}
            <span className="text-[#D88C7A]" data-id="element-28">SABRÁS QUÉ HACER</span>
          </h2>
          <div className="flex justify-center gap-6 text-lg md:text-xl text-[#3A3A3A]/80 font-semibold" data-id="element-29">
            <span data-id="element-30">✔ Sin gritos</span>
            <span data-id="element-31">✔ Sin culpa</span>
            <span data-id="element-32">✔ Con seguridad</span>
          </div>
          <p className="text-xl md:text-2xl font-extrabold text-[#D88C7A]" data-id="element-33">
            🔥 Empieza hoy mismo
          </p>
        </div>
      </section>

      {/* 3. PRESENTACIÓN */}
      <section className="py-20 px-4 bg-[#EDF1EA]" data-id="element-34">
        <div className="max-w-5xl mx-auto" data-id="element-35">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-36">
            <div className="order-2 md:order-1" data-id="element-37">
              <h2 className="text-3xl md:text-5xl font-extrabold text-[#3A3A3A] mb-6 leading-tight" data-id="element-38">
                NO VAS A VOLVER A SENTIRTE{' '}
                <span className="text-[#D88C7A]" data-id="element-39">PERDIDA EN UNA RABIETA</span>
              </h2>
              <p className="text-[#3A3A3A]/80 text-lg mb-6 leading-relaxed" data-id="element-40">
                Aquí tienes exactamente qué hacer, qué decir y cómo actuar en el
                momento más difícil…{' '}
                <span className="font-bold text-[#D88C7A]" data-id="element-41">
                  sin gritar, sin culpa y sin perder el control.
                </span>
              </p>
              <div className="space-y-4 mb-6" data-id="element-42">
                {['Qué decir en el momento exacto', 'Cómo calmar sin gritar ni explotar', 'Cómo poner límites sin culpa', 'Cómo reaccionar con seguridad incluso cuando estás cansada'].map((item, i) => <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-white border border-[#E8CFCF]" data-id="element-43">
                    <div className="bg-[#E8CFCF] text-[#A8B5A2] p-1 rounded-full mt-0.5" data-id="element-44">
                      <CheckIcon className="w-4 h-4" data-id="element-45" />
                    </div>
                    <p className="text-[#3A3A3A]/80 font-medium" data-id="element-46">{item}</p>
                  </div>)}
              </div>
              <div className="p-6 bg-[#F5EAEA] rounded-xl border-2 border-[#D88C7A]/30" data-id="element-47">
                <p className="text-[#D88C7A] font-extrabold text-lg" data-id="element-48">
                  🔥 Y para que realmente lo apliques en tu día a día… también
                  vas a recibir herramientas extra que lo hacen aún más fácil.
                </p>
              </div>
            </div>
            <div className="order-1 md:order-2 flex justify-center" data-id="element-49">
              <div className="relative w-full max-w-md aspect-[3/4] bg-[#F5EAEA] rounded-2xl overflow-hidden shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-50">
                <img src="https://cdn.magicpatterns.com/uploads/sgub4mCNwa77CEwf65p5AM/IMG_1743_segunda_imagen_listo_comprimida_.png" alt="Guía de Crianza con Inteligencia Emocional" className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-51" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. BONUSES - REGALOS EXCLUSIVOS */}
      <section className="py-20 px-4 bg-white" data-id="element-52">
        <div className="max-w-6xl mx-auto" data-id="element-53">
          <div className="text-center mb-12" data-id="element-54">
            <span className="bg-gradient-to-r from-[#D88C7A] to-[#C57665] text-white px-8 py-3 rounded-full text-lg md:text-xl font-bold mb-6 inline-block shadow-xl shadow-[#D88C7A]/40 uppercase tracking-widest" data-id="element-55">
              🎁 REGALOS EXCLUSIVOS
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] uppercase tracking-wide" data-id="element-56">
              LLÉVATE ESTOS 3 BONOS COMPLETAMENTE GRATIS — ¡SOLO POR HOY!
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-57">
            <BonusCard title="Tarjetas Emocionales Ilustradas" value="$35" icon="🎨" description="💡 Perfectas para el hogar, rutinas y momentos difíciles." imageUrl="https://cdn.magicpatterns.com/uploads/r2Eain3NhY8uswxR2u7s41/IMG_0180_bono_1_comprimida_.jpg" benefits={['Identificar lo que sienten', 'Expresar emociones sin berrinches', 'Desarrollar inteligencia emocional desde pequeños']} delay={0.1} data-id="element-58" />
            <BonusCard title="Cómo Crear Tus Propias Tarjetas Emocionales en Casa" value="$47" icon="✂️" description="✨ Una habilidad que podrás usar para siempre." imageUrl="https://cdn.magicpatterns.com/uploads/ucJVeMF2xm8STar4bH2HBU/IMG_0181_bono_2_comprimida_.jpg" benefits={['Diseñar tarjetas personalizadas según la edad de tus hijos', 'Adaptarlas a situaciones reales del día a día', 'Convertirlas en una herramienta de conexión familiar']} delay={0.2} data-id="element-59" />
            <BonusCard title="Relajación Emocional para Mamás" value="$55" icon="🧘‍♀️" description="🌿 Diseñado para mamás reales, con poco tiempo." imageUrl="https://cdn.magicpatterns.com/uploads/sjJC2srZiK2cgjGm8nTHm5/IMG_0182_Bono_3_Comprimida_.jpg" benefits={['Ejercicios de descarga emocional', 'Técnicas de calma después de días difíciles', 'Prácticas cortas para recuperar energía mental']} delay={0.3} data-id="element-60" />
          </div>
        </div>
      </section>

      {/* 5. BLOQUE PREMIUM - AQUÍ ES DONDE TODO CAMBIA */}
      <section className="py-20 px-4 bg-white" data-id="element-61">
        <div className="max-w-6xl mx-auto" data-id="element-62">
          <div className="text-center mb-12" data-id="element-63">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] mb-6 uppercase tracking-wide" data-id="element-64">
              🎁 AQUÍ ES DONDE TODO CAMBIA
            </h2>
            <p className="text-[#3A3A3A]/80 text-lg mb-4 max-w-3xl mx-auto" data-id="element-65">
              No solo aprenderás…
            </p>
            <p className="text-[#3A3A3A]/80 text-lg mb-8 max-w-3xl mx-auto font-semibold" data-id="element-66">
              También tendrás herramientas listas para usar en el momento
              exacto:
            </p>
          </div>

          <div className="bg-[#F5EAEA] rounded-3xl p-8 md:p-12 shadow-xl border border-[#E8CFCF] flex flex-col md:flex-row gap-12 items-center" data-id="element-67">
            <div className="w-full md:w-1/2" data-id="element-68">
              <img src="https://cdn.magicpatterns.com/uploads/sgub4mCNwa77CEwf65p5AM/IMG_1743_segunda_imagen_listo_comprimida_.png" alt="Portada de la Guía de Crianza" className="w-full rounded-xl shadow-lg transform md:-rotate-2 hover:rotate-0 transition-transform duration-500" loading="lazy" decoding="async" data-id="element-69" />
            </div>

            <div className="w-full md:w-1/2 space-y-6" data-id="element-70">
              <ul className="space-y-4" data-id="element-71">
                {['Tarjetas imprimibles para regular emociones', 'Frases exactas para momentos de crisis', 'Técnicas prácticas que funcionan incluso cuando estás cansada', 'Apoyo visual para ayudar a tu hijo a calmarse'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-72">
                    <div className="bg-white text-[#A8B5A2] p-1 rounded-full mt-1" data-id="element-73">
                      <CheckIcon className="w-4 h-4" data-id="element-74" />
                    </div>
                    <span className="text-[#3A3A3A]/80 text-lg font-semibold" data-id="element-75">
                      {item}
                    </span>
                  </li>)}
              </ul>
              <div className="flex flex-col items-center gap-4 mt-4" data-id="element-76">
                <a href="https://43skvt-1y.myshopify.com/products/guia-de-crianza-con-inteligencia-emocional" target="_blank" rel="noopener noreferrer" className="block w-full bg-[#D88C7A] hover:bg-[#C57665] text-white font-extrabold py-5 px-8 rounded-xl shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all duration-300 text-center animate-pulse" data-id="element-77">
                  <span className="block text-3xl md:text-4xl mb-1" data-id="element-78">
                    $12.75 USD
                  </span>
                  <span className="block text-base md:text-lg" data-id="element-79">
                    👉 ESTO ES LO QUE REALMENTE TRANSFORMA TU FORMA DE
                    REACCIONAR COMO MAMÁ — ¡EMPIEZA HOY!
                  </span>
                </a>
                <div className="flex flex-wrap justify-center gap-4 text-sm text-[#3A3A3A]/70 font-semibold" data-id="element-80">
                  <span data-id="element-81">✔ Acceso inmediato</span>
                  <span data-id="element-82">✔ Descarga instantánea</span>
                  <span data-id="element-83">✔ Pago 100% seguro</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. PREMIUM UPGRADE SECTION */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#F7F3EF] to-[#F5EAEA] border-t-4 border-[#D88C7A]/30" data-id="element-84">
        <div className="max-w-4xl mx-auto" data-id="element-85">
          <div className="text-center mb-12" data-id="element-86">
            <a href="https://43skvt-1y.myshopify.com/products/guia-de-crianza-con-inteligencia-emocional-premium" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#D88C7A] to-[#C57665] text-white px-10 py-4 rounded-full text-lg md:text-xl font-bold mb-8 inline-flex items-center gap-3 shadow-2xl shadow-[#D88C7A]/50 uppercase tracking-widest animate-pulse hover:shadow-3xl hover:scale-105 transition-all" data-id="element-87">
              ⭐ LA OPCIÓN MÁS ELEGIDA
            </a>
            <h2 className="text-4xl md:text-6xl font-extrabold text-[#D88C7A] mt-6 mb-8 leading-tight uppercase tracking-wide" data-id="element-88">
              ACCESO PREMIUM COMPLETO
            </h2>
            <div className="mb-10 flex justify-center" data-id="element-89">
              <img src="https://cdn.magicpatterns.com/uploads/ckwZ9hJYNaT3pvKqLNDuWp/IMG_0282.jpg" alt="Técnicas para Regular las Molestias de tu Hijo - Versión Premium" className="w-full max-w-2xl rounded-2xl shadow-xl" loading="lazy" decoding="async" data-id="element-90" />
            </div>
            <p className="text-xl md:text-2xl font-bold text-[#3A3A3A]/80 mb-3 uppercase tracking-wide" data-id="element-91">
              NO VUELVAS A IMPROVISAR EN UNA RABIETA
            </p>
            <p className="text-xl md:text-2xl text-[#3A3A3A]/70 mb-8 font-medium" data-id="element-92">
              Sabrás qué hacer, qué decir y cómo actuar — sin gritar, sin culpa.
            </p>

            <div className="grid grid-cols-2 gap-3 max-w-md mx-auto mb-8" data-id="element-93">
              {[{
              icon: '📖',
              text: 'Guía paso a paso'
            }, {
              icon: '🃏',
              text: 'Tarjetas listas para usar'
            }, {
              icon: '🧘',
              text: 'Técnicas para calmar sin gritar'
            }, {
              icon: '🛡️',
              text: 'Herramientas para actuar segura'
            }].map((item, i) => <div key={i} className="bg-white/80 border border-[#E8CFCF]/50 rounded-xl p-4 text-center" data-id="element-94">
                  <span className="text-2xl block mb-1" data-id="element-95">{item.icon}</span>
                  <span className="text-[#3A3A3A]/80 font-semibold text-sm" data-id="element-96">
                    {item.text}
                  </span>
                </div>)}
            </div>

            <div className="flex justify-center items-baseline gap-2 mb-4" data-id="element-97">
              <span className="text-6xl font-extrabold text-[#D88C7A]" data-id="element-98">
                $15.99
              </span>
              <span className="text-xl text-[#3A3A3A]/50" data-id="element-99">USD</span>
            </div>
            <a href="https://43skvt-1y.myshopify.com/products/guia-de-crianza-con-inteligencia-emocional-premium" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#D88C7A] hover:bg-[#C57665] text-white font-extrabold text-xl py-5 px-12 rounded-xl shadow-2xl transition-all transform hover:scale-105 text-center mb-4" data-id="element-100">
              🔥 QUIERO EL ACCESO PREMIUM COMPLETO
            </a>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-[#3A3A3A]/70 font-semibold mb-4" data-id="element-101">
              <span data-id="element-102">✔ Acceso inmediato</span>
              <span data-id="element-103">✔ Descarga instantánea</span>
              <span data-id="element-104">✔ Pago seguro</span>
            </div>
            <p className="text-sm text-[#D88C7A] font-bold italic" data-id="element-105">
              💛 Empieza hoy mismo a manejar las rabietas con calma y seguridad.
            </p>
          </div>

          {/* QUÉ INCLUYE */}
          <div className="bg-white rounded-3xl p-8 md:p-10 mb-12 border-4 border-[#D88C7A]/20 shadow-2xl" data-id="element-106">
            <h3 className="text-2xl md:text-3xl font-bold text-[#3A3A3A] mb-8 text-center uppercase tracking-wide" data-id="element-107">
              ✨ TODO ESTO ESTÁ INCLUIDO:
            </h3>
            <div className="grid md:grid-cols-2 gap-5" data-id="element-108">
              {['Tarjetas listas para usar en el momento', 'Frases que funcionan en la vida real', 'Juegos emocionales para calmar a tu hijo', 'Herramientas probadas para evitar gritos'].map((item, i) => <div key={i} className="flex items-start gap-3 bg-[#EDF1EA]/50 p-4 rounded-xl" data-id="element-109">
                  <CheckIcon className="w-6 h-6 text-[#A8B5A2] flex-shrink-0 mt-0.5" data-id="element-110" />
                  <span className="text-[#3A3A3A]/80 font-bold" data-id="element-111">{item}</span>
                </div>)}
            </div>
            <div className="mt-8 p-6 bg-[#F5EAEA] rounded-2xl border-2 border-[#D88C7A]/30" data-id="element-112">
              <p className="text-[#D88C7A] font-bold text-xl text-center uppercase" data-id="element-113">
                🔥 NO ES TEORÍA. ES ACCIÓN INMEDIATA PARA TU DÍA A DÍA.
              </p>
            </div>
          </div>

          {/* BLOQUE DESTACADO - BONO PREMIUM */}
          <div className="bg-[#F5EAEA] rounded-3xl p-8 md:p-10 mb-12 border-2 border-[#E8CFCF] relative overflow-hidden shadow-lg" data-id="element-114">
            <div className="absolute top-0 right-0 bg-[#D88C7A] text-white px-6 py-2 rounded-bl-2xl font-bold text-sm" data-id="element-115">
              BONO PREMIUM EXCLUSIVO
            </div>
            <div className="mt-6 md:mt-2 flex flex-col md:flex-row gap-8 items-center" data-id="element-116">
              <div className="w-full md:w-2/5 flex-shrink-0" data-id="element-117">
                <img src="https://cdn.magicpatterns.com/uploads/nM4jdonaD8gYfAZbFuhLAh/IMG_1737.jpg" alt="Técnicas para Regular las Molestias de tu Hijo" className="w-full rounded-2xl shadow-lg" loading="lazy" decoding="async" data-id="element-118" />
              </div>
              <div className="w-full md:w-3/5" data-id="element-119">
                <h3 className="text-2xl md:text-3xl font-bold text-[#3A3A3A] mb-4" data-id="element-120">
                  Técnicas para Regular las Molestias de tu Hijo
                </h3>
                <p className="text-[#3A3A3A]/80 text-lg mb-8" data-id="element-121">
                  Un sistema práctico de tarjetas imprimibles diseñado para
                  ayudarte a actuar con seguridad en momentos de rabietas,
                  frustración o llanto.
                </p>

                <div className="space-y-4 mb-8" data-id="element-122">
                  {['Frases exactas que puedes decir en momentos difíciles', 'Técnicas efectivas para calmar sin gritar ni castigar', 'Estrategias para acompañar emociones intensas', 'Herramientas visuales fáciles de usar con tu hijo'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-123">
                      <div className="bg-[#E8CFCF] text-[#D88C7A] p-1 rounded-full mt-0.5 flex-shrink-0" data-id="element-124">
                        <CheckIcon className="w-4 h-4" data-id="element-125" />
                      </div>
                      <span className="text-[#3A3A3A]/80" data-id="element-126">{item}</span>
                    </div>)}
                </div>

                <div className="bg-white/60 rounded-xl p-6 border border-[#E8CFCF]/50" data-id="element-127">
                  <p className="text-[#D88C7A] font-bold text-lg mb-2" data-id="element-128">
                    👉 Ya no tendrás que improvisar
                  </p>
                  <p className="text-[#D88C7A] font-bold text-lg" data-id="element-129">
                    👉 Tendrás claridad en cada situación
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* BLOQUE EMOCIONAL */}
          <div className="text-center mb-12 max-w-3xl mx-auto" data-id="element-130">
            <p className="text-2xl md:text-3xl text-[#3A3A3A] font-bold mb-8 uppercase tracking-wide" data-id="element-131">
              SABEMOS LO DIFÍCIL QUE ES CUANDO TU HIJO:
            </p>
            <div className="space-y-4 mb-8" data-id="element-132">
              {['😤 Tiene una rabieta inesperada y no sabes cómo reaccionar', '😢 Llora sin parar y nada de lo que haces funciona', '😣 Se frustra y no sabe cómo expresar lo que siente'].map((item, i) => <div key={i} className="bg-[#F5EAEA] p-5 rounded-xl border border-[#E8CFCF]/50" data-id="element-133">
                  <p className="text-[#3A3A3A]/80 text-lg font-bold" data-id="element-134">{item}</p>
                </div>)}
            </div>
            <div className="bg-gradient-to-r from-[#D88C7A]/10 to-[#C57665]/10 p-8 rounded-2xl border-2 border-[#D88C7A]/30 shadow-lg" data-id="element-135">
              <p className="text-[#3A3A3A] font-bold text-xl md:text-2xl text-center uppercase tracking-wide leading-relaxed" data-id="element-136">
                ESTA VERSIÓN PREMIUM NO SOLO TE ENSEÑA…
              </p>
              <p className="text-[#D88C7A] font-bold text-2xl md:text-3xl text-center uppercase tracking-wide mt-3" data-id="element-137">
                🔥 TE GUÍA PASO A PASO EN EL MOMENTO EXACTO DONDE TODO OCURRE.
              </p>
            </div>
          </div>

          {/* PRICING PREMIUM */}
          <div className="bg-[#F7F3EF] text-[#3A3A3A] rounded-3xl p-8 md:p-12 max-w-lg mx-auto shadow-2xl border-2 border-[#D88C7A]/30 text-center relative" data-id="element-138">
            <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-[#D88C7A] to-[#C57665] text-white px-6 py-2 rounded-full font-bold shadow-md w-max" data-id="element-139">
              ⭐ LA OPCIÓN MÁS ELEGIDA
            </div>

            <h3 className="text-2xl md:text-3xl font-extrabold text-[#3A3A3A] mt-4 mb-2" data-id="element-140">
              SABRÁS EXACTAMENTE QUÉ HACER EN CADA RABIETA
            </h3>

            <p className="text-[#3A3A3A]/70 mb-6 font-medium" data-id="element-141">
              Sin gritar. Sin culpa. Sin perder el control.
            </p>

            <div className="space-y-3 text-left max-w-xs mx-auto mb-6" data-id="element-142">
              {['Qué decir en el momento exacto', 'Cómo calmar sin explotar', 'Cómo poner límites con seguridad', 'Qué hacer incluso cuando estás cansada'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-143">
                  <CheckIcon className="w-5 h-5 text-[#D88C7A] flex-shrink-0 mt-0.5" data-id="element-144" />
                  <span className="text-[#3A3A3A]/80 font-semibold" data-id="element-145">
                    {item}
                  </span>
                </div>)}
            </div>

            <div className="flex justify-center items-baseline gap-2 mb-6" data-id="element-146">
              <span className="text-6xl font-extrabold text-[#D88C7A]" data-id="element-147">
                $15.99
              </span>
              <span className="text-xl text-[#3A3A3A]/50" data-id="element-148">USD</span>
            </div>

            <a href="https://43skvt-1y.myshopify.com/products/guia-de-crianza-con-inteligencia-emocional-premium" target="_blank" rel="noopener noreferrer" className="block w-full bg-[#D88C7A] hover:bg-[#C57665] text-white font-extrabold text-xl py-5 px-8 rounded-xl shadow-2xl transition-all transform hover:scale-105 text-center mb-4" data-id="element-149">
              🔥 QUIERO DEJAR DE IMPROVISAR DESDE HOY
            </a>

            <div className="flex flex-wrap justify-center gap-4 text-sm text-[#3A3A3A]/70 font-semibold mb-4" data-id="element-150">
              <span data-id="element-151">✔ Acceso inmediato</span>
              <span data-id="element-152">✔ Descarga instantánea</span>
              <span data-id="element-153">✔ Pago 100% seguro</span>
            </div>

            <div className="border-t border-[#E8CFCF]/40 pt-4 mt-2" data-id="element-154">
              <p className="text-sm text-[#D88C7A] font-bold italic text-center" data-id="element-155">
                Empieza hoy a manejar las rabietas con calma y seguridad.
              </p>
            </div>
          </div>

          {/* TESTIMONIOS PREMIUM */}
          <div className="mt-16" data-id="element-156">
            <h3 className="text-2xl font-bold text-[#3A3A3A] text-center mb-10" data-id="element-157">
              💬 Lo que dicen las mamás sobre el plan premium
            </h3>

            <div className="bg-[#F5EAEA] border-2 border-[#D88C7A]/30 rounded-3xl p-8 md:p-10 mb-8 shadow-lg relative" data-id="element-158">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#D88C7A] text-white px-5 py-1.5 rounded-full font-bold text-sm shadow-md" data-id="element-159">
                ⭐ TESTIMONIO DESTACADO
              </div>
              <p className="text-[#3A3A3A]/80 text-lg italic leading-relaxed mb-4 mt-2" data-id="element-160">
                "Este fue el bono que realmente hizo la diferencia. El ebook me
                ayudó a entender, pero estas tarjetas me dieron herramientas
                reales para el día a día. Ahora cuando mi hijo se frustra, ya sé
                qué hacer."
              </p>
              <div className="flex items-center gap-3" data-id="element-161">
                <div className="w-10 h-10 bg-[#E8CFCF] rounded-full flex items-center justify-center text-[#D88C7A] font-bold" data-id="element-162">
                  C
                </div>
                <div data-id="element-163">
                  <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-164">Carolina</p>
                  <p className="text-[#3A3A3A]/50 text-xs" data-id="element-165">
                    Mamá de niño de 2 años
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-166">
              <div className="bg-white p-6 rounded-2xl border border-[#E8CFCF]/40 shadow-sm" data-id="element-167">
                <p className="text-[#3A3A3A]/70 italic leading-relaxed mb-4" data-id="element-168">
                  "Las tarjetas son lo mejor que he comprado para mi hijo. Son
                  tan fáciles de usar que incluso en medio de una rabieta puedo
                  aplicarlas. Se siente todo mucho más tranquilo en casa."
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-3" data-id="element-169">
                  <div className="w-9 h-9 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold text-sm" data-id="element-170">
                    V
                  </div>
                  <div data-id="element-171">
                    <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-172">Valeria</p>
                    <p className="text-[#3A3A3A]/50 text-xs" data-id="element-173">
                      Mamá de niño de 3 años
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E8CFCF]/40 shadow-sm" data-id="element-174">
                <p className="text-[#3A3A3A]/70 italic leading-relaxed mb-4" data-id="element-175">
                  "Por fin algo práctico y no solo teoría. Me encantó porque no
                  tengo que pensar qué hacer… simplemente uso las técnicas y
                  funciona."
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-3" data-id="element-176">
                  <div className="w-9 h-9 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold text-sm" data-id="element-177">
                    D
                  </div>
                  <div data-id="element-178">
                    <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-179">Daniela</p>
                    <p className="text-[#3A3A3A]/50 text-xs" data-id="element-180">
                      Mamá de niño de 4 años
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E8CFCF]/40 shadow-sm" data-id="element-181">
                <p className="text-[#3A3A3A]/70 italic leading-relaxed mb-4" data-id="element-182">
                  "Mi hijo ahora logra calmarse más rápido. Antes todo terminaba
                  en llanto, ahora con estas técnicas puedo ayudarlo a
                  regularse."
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-3" data-id="element-183">
                  <div className="w-9 h-9 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold text-sm" data-id="element-184">
                    L
                  </div>
                  <div data-id="element-185">
                    <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-186">Laura</p>
                    <p className="text-[#3A3A3A]/50 text-xs" data-id="element-187">
                      Mamá de niño de 2 años
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E8CFCF]/40 shadow-sm" data-id="element-188">
                <p className="text-[#3A3A3A]/70 italic leading-relaxed mb-4" data-id="element-189">
                  "Vale completamente la pena el plan premium. Las tarjetas y
                  las técnicas me dieron seguridad como mamá. Es justo lo que
                  necesitaba para dejar de sentirme perdida."
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-3" data-id="element-190">
                  <div className="w-9 h-9 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold text-sm" data-id="element-191">
                    A
                  </div>
                  <div data-id="element-192">
                    <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-193">Andrea</p>
                    <p className="text-[#3A3A3A]/50 text-xs" data-id="element-194">
                      Mamá de niño de 3 años
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA FINAL PREMIUM */}
            <div className="mt-12 text-center bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-[#D88C7A]/30" data-id="element-195">
              <p className="text-xl md:text-2xl text-[#3A3A3A] font-bold mb-2 uppercase tracking-wide" data-id="element-196">
                🔥 ACCESO PREMIUM COMPLETO POR SOLO
              </p>
              <p className="text-6xl md:text-7xl font-bold text-[#D88C7A] mb-2" data-id="element-197">
                $15.99 <span className="text-xl text-[#3A3A3A]/50" data-id="element-198">USD</span>
              </p>
              <p className="text-[#D88C7A] font-bold mb-8 uppercase" data-id="element-199">
                ⚡ ESTE PRECIO NO ESTARÁ DISPONIBLE POR MUCHO TIEMPO
              </p>
              <a href="https://43skvt-1y.myshopify.com/products/guia-de-crianza-con-inteligencia-emocional-premium" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold text-xl md:text-2xl py-5 px-12 rounded-xl shadow-lg transition-all transform hover:scale-105 text-center" data-id="element-200">
                🔥 SÍ, QUIERO EL ACCESO PREMIUM AHORA
              </a>
              <p className="mt-4 text-sm text-[#3A3A3A]/50" data-id="element-201">
                🔒 PAGO 100% SEGURO • ACCESO INMEDIATO Y DE POR VIDA 💛
              </p>
              <p className="mt-4 text-lg text-[#D88C7A] font-bold uppercase" data-id="element-202">
                💛 PARA MAMÁS QUE QUIEREN SABER EXACTAMENTE QUÉ HACER EN LOS
                MOMENTOS MÁS DIFÍCILES.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. ADIÓS RABIETAS EN LA MESA */}
      <section className="py-20 px-4 bg-[#EDF1EA]" data-id="element-203">
        <div className="max-w-4xl mx-auto" data-id="element-204">
          <p className="text-center text-xl md:text-2xl text-[#D88C7A] font-bold mb-6" data-id="element-205">
            💛 También puedes empezar solo con herramientas prácticas
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] mb-4 text-center uppercase tracking-wide" data-id="element-206">
            ADIÓS RABIETAS EN LA MESA + DIVERSIÓN SIN PANTALLAS
          </h2>
          <p className="text-[#3A3A3A]/70 text-lg text-center mb-10" data-id="element-207">
            Un sistema de tarjetas y juegos imprimibles para usar en casa
          </p>

          <div className="flex justify-center mb-10" data-id="element-208">
            <img src="https://cdn.magicpatterns.com/uploads/pMrfhtv6AvfReYfxNUrYUJ/imagen_fomenta_la_conexion_1_comprimida_2.png" alt="Adiós Rabietas en la Mesa + Diversión sin Pantallas" className="w-full max-w-2xl rounded-2xl shadow-xl" loading="lazy" decoding="async" data-id="element-209" />
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-[#A8B5A2]/20" data-id="element-210">
            <div className="space-y-4 mb-8" data-id="element-211">
              {['60 tarjetas de alimentación sin estrés', '60 tarjetas de juegos sin pantallas', 'Actividades listas para aplicar', 'Ideal para reducir rabietas en la rutina diaria'].map((item, i) => <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-[#F5EAEA] border border-[#E8CFCF]" data-id="element-212">
                  <div className="bg-[#E8CFCF] text-[#A8B5A2] p-1 rounded-full mt-0.5" data-id="element-213">
                    <CheckIcon className="w-4 h-4" data-id="element-214" />
                  </div>
                  <p className="text-[#3A3A3A]/80 font-medium" data-id="element-215">{item}</p>
                </div>)}
            </div>

            <div className="bg-[#F5EAEA] p-6 rounded-2xl border border-[#E8CFCF] mb-6" data-id="element-216">
              <p className="text-[#3A3A3A] font-bold text-lg text-center" data-id="element-217">
                👉 Solo imprimes y empiezas
              </p>
              <p className="text-[#3A3A3A]/70 text-center mt-2" data-id="element-218">
                Perfecto si necesitas soluciones rápidas sin leer todo el
                contenido
              </p>
            </div>

            <div className="bg-[#F7F3EF] text-[#3A3A3A] rounded-3xl p-8 md:p-12 max-w-lg mx-auto shadow-2xl border-2 border-[#6B9BD1]/30 text-center relative" data-id="element-219">
              <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-[#6B9BD1] text-white px-6 py-2 rounded-full font-bold shadow-md w-max" data-id="element-220">
                SOLO TARJETAS IMPRIMIBLES
              </div>

              <p className="text-[#3A3A3A]/70 mt-4 mb-6 font-medium" data-id="element-221">
                Acceso al pack esencial de tarjetas en PDF
              </p>

              <div className="flex justify-center items-baseline gap-2 mb-8" data-id="element-222">
                <span className="text-6xl font-bold text-[#6B9BD1]" data-id="element-223">$9.99</span>
                <span className="text-xl text-[#3A3A3A]/50" data-id="element-224">USD</span>
              </div>

              <a href="https://43skvt-1y.myshopify.com/products/adios-rabietas-en-la-mesa-diversion-sin-pantalla-tarjetas-imprimibles" target="_blank" rel="noopener noreferrer" className="block w-full bg-[#6B9BD1] hover:bg-[#5a8ac0] text-white font-bold text-xl py-4 px-8 rounded-xl shadow-lg transition-all transform hover:scale-105 text-center mb-6" data-id="element-225">
                QUIERO SOLO LAS TARJETAS IMPRIMIBLES
              </a>

              <div className="flex flex-wrap justify-center gap-4 text-sm text-[#3A3A3A]/70 font-semibold mb-4" data-id="element-226">
                <span data-id="element-227">✔ Acceso inmediato</span>
                <span data-id="element-228">✔ Descarga instantánea</span>
                <span data-id="element-229">✔ Pago 100% seguro</span>
              </div>

              <div className="border-t border-[#E8CFCF]/40 pt-4 mt-4" data-id="element-230">
                <p className="text-sm text-[#6B9BD1] font-bold italic text-center" data-id="element-231">
                  💛 Perfecto para mamás que quieren una solución rápida y
                  práctica para mejorar la hora de la comida.
                </p>
              </div>
            </div>
          </div>

          {/* TESTIMONIOS TARJETAS */}
          <div className="mt-12" data-id="element-232">
            <h3 className="text-2xl md:text-3xl font-bold text-[#3A3A3A] mb-8 text-center uppercase tracking-wide" data-id="element-233">
              LO QUE OTRAS MAMÁS YA ESTÁN LOGRANDO EN LA HORA DE COMER
            </h3>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-234">
              {[{
              quote: 'Antes la hora de comer era un caos… ahora mi hijo participa, juega y come sin peleas. Esto cambió completamente nuestras comidas.',
              author: 'María L.'
            }, {
              quote: 'Las tarjetas fueron lo mejor… mi hijo se distrae con los juegos y ya no pide el celular en la mesa.',
              author: 'Andrea P.'
            }, {
              quote: 'Ya no tengo que rogarle para que coma. Ahora todo fluye mucho más tranquilo.',
              author: 'Daniela R.'
            }, {
              quote: 'Pasamos de gritos y estrés… a momentos tranquilos y hasta divertidos en familia.',
              author: 'Laura M.'
            }, {
              quote: 'Lo uso TODOS los días. Es práctico, fácil y realmente funciona con niños pequeños.',
              author: 'Sofía C.'
            }, {
              quote: 'Mi hijo ahora espera la hora de comer… porque sabe que habrá juegos y actividades.',
              author: 'Valentina T.'
            }, {
              quote: 'Dejé de usar pantallas en la mesa y fue lo mejor que pude hacer. Estas tarjetas lo hicieron posible.',
              author: 'Camila R.'
            }, {
              quote: 'Pensé que no iba a funcionar… pero desde el primer día vi el cambio.',
              author: 'Paula M.'
            }, {
              quote: 'Ahora comemos en paz. Sin distracciones, sin peleas y con más conexión.',
              author: 'Gabriela S.'
            }, {
              quote: 'Es justo lo que necesitaba… algo práctico que me dijera qué hacer en ese momento.',
              author: 'Andrea V.'
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.05
            }} className="bg-white p-6 rounded-2xl shadow-sm border border-[#E8CFCF]/40 relative" data-id="element-235">
                  <div className="absolute -top-3 left-6 bg-[#F5EAEA] text-[#D88C7A] p-2 rounded-lg" data-id="element-236">
                    <span className="text-xl" data-id="element-237">❝</span>
                  </div>
                  <p className="text-[#3A3A3A]/70 italic leading-relaxed pt-4" data-id="element-238">
                    "{item.quote}"
                  </p>
                  <p className="text-[#D88C7A] font-bold text-sm mt-3" data-id="element-239">
                    – {item.author}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 8. BLOQUE DECISIÓN */}
      <section className="py-16 px-4 bg-white border-y border-[#E8CFCF]/30" data-id="element-240">
        <div className="max-w-3xl mx-auto" data-id="element-241">
          <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] mb-8 text-center uppercase tracking-wide" data-id="element-242">
            ¿QUÉ NECESITAS HOY?
          </h2>
          <div className="space-y-4 mb-6" data-id="element-243">
            {['Si quieres entender qué hacer → Guía', 'Si necesitas ayuda inmediata → Tarjetas', 'Si quieres el cambio completo → Kit Completo'].map((item, i) => <div key={i} className="flex items-start gap-3 p-5 rounded-xl bg-[#F5EAEA] border border-[#E8CFCF]" data-id="element-244">
                <span className="text-[#A8B5A2] text-xl mt-0.5" data-id="element-245">✔</span>
                <p className="text-[#3A3A3A]/80 text-lg font-semibold" data-id="element-246">
                  {item}
                </p>
              </div>)}
          </div>
          <div className="bg-[#EDF1EA] p-6 rounded-2xl border border-[#A8B5A2]/30 text-center" data-id="element-247">
            <a href="https://43skvt-1y.myshopify.com/collections/frontpage" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold text-lg py-4 px-8 rounded-xl shadow-lg transition-all hover:scale-105 text-center" data-id="element-248">
              👉 TÚ DECIDES CÓMO EMPEZAR
            </a>
          </div>
        </div>
      </section>

      {/* 9. ELIGE LA MEJOR OPCIÓN PARA TI */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#F7F3EF] via-[#F5EAEA] to-[#EDF1EA]" data-id="element-249">
        <div className="max-w-6xl mx-auto" data-id="element-250">
          <h2 className="text-4xl md:text-5xl font-bold text-[#3A3A3A] mb-16 text-center uppercase tracking-wide" data-id="element-251">
            ELIGE LA MEJOR OPCIÓN PARA TI
          </h2>

          <div className="grid md:grid-cols-3 gap-8 items-start" data-id="element-252">
            {/* OPCIÓN 1: GUÍA DIGITAL */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-[#A8B5A2]/40 hover:shadow-2xl transition-shadow duration-300" data-id="element-253">
              <div className="text-center mb-8" data-id="element-254">
                <span className="inline-block bg-[#A8B5A2] text-white px-5 py-2.5 rounded-full text-sm font-bold mb-5 shadow-md" data-id="element-255">
                  🟢 OPCIÓN 1
                </span>
                <h3 className="text-3xl font-extrabold text-[#3A3A3A] mb-2 tracking-tight" data-id="element-256">
                  GUÍA DIGITAL
                </h3>
                <p className="text-[#D88C7A] text-base font-bold mb-3" data-id="element-257">
                  Guía de crianza con inteligencia emocional + bonos
                </p>
                <p className="text-[#3A3A3A]/60 text-sm italic" data-id="element-258">
                  Aprende cómo manejar rabietas y emociones paso a paso
                </p>
              </div>

              <div className="space-y-4 mb-8" data-id="element-259">
                {['Entiende lo que le pasa a tu hijo', 'Aprende qué hacer en cada situación', 'Gana seguridad como mamá'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-260">
                    <CheckIcon className="w-5 h-5 text-[#A8B5A2] flex-shrink-0 mt-0.5" data-id="element-261" />
                    <span className="text-[#3A3A3A]/80 text-base font-medium" data-id="element-262">
                      {item}
                    </span>
                  </div>)}
              </div>

              <div className="text-center mb-8 py-4 bg-[#EDF1EA]/50 rounded-2xl" data-id="element-263">
                <span className="text-5xl font-extrabold text-[#3A3A3A]" data-id="element-264">
                  $12.75
                </span>
              </div>

              <a href="https://43skvt-1y.myshopify.com/products/guia-de-crianza-con-inteligencia-emocional" target="_blank" rel="noopener noreferrer" className="block w-full bg-[#A8B5A2] hover:bg-[#96a590] text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all hover:scale-105 text-center text-lg" data-id="element-265">
                QUIERO LA GUÍA
              </a>
              <div className="flex flex-wrap justify-center gap-3 text-xs text-[#3A3A3A]/70 font-semibold mt-4" data-id="element-266">
                <span data-id="element-267">✔ Acceso inmediato</span>
                <span data-id="element-268">✔ Descarga instantánea</span>
                <span data-id="element-269">✔ Pago 100% seguro</span>
              </div>
            </div>

            {/* OPCIÓN 2: TARJETAS IMPRIMIBLES */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-[#6B9BD1]/40 hover:shadow-2xl transition-shadow duration-300" data-id="element-270">
              <div className="text-center mb-8" data-id="element-271">
                <span className="inline-block bg-[#6B9BD1] text-white px-5 py-2.5 rounded-full text-sm font-bold mb-5 shadow-md" data-id="element-272">
                  🔵 OPCIÓN 2
                </span>
                <h3 className="text-3xl font-extrabold text-[#3A3A3A] mb-2 tracking-tight" data-id="element-273">
                  TARJETAS IMPRIMIBLES
                </h3>
                <p className="text-[#D88C7A] text-base font-bold mb-3" data-id="element-274">
                  Adiós rabietas en la mesa + diversión sin pantallas
                </p>
                <p className="text-[#3A3A3A]/60 text-sm italic" data-id="element-275">
                  Soluciones prácticas para aplicar hoy mismo
                </p>
              </div>

              <div className="space-y-4 mb-8" data-id="element-276">
                {['60 tarjetas de alimentación', '60 juegos sin pantallas', 'Actividades listas', 'Uso inmediato'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-277">
                    <CheckIcon className="w-5 h-5 text-[#6B9BD1] flex-shrink-0 mt-0.5" data-id="element-278" />
                    <span className="text-[#3A3A3A]/80 text-base font-medium" data-id="element-279">
                      {item}
                    </span>
                  </div>)}
              </div>

              <div className="text-center mb-8 py-4 bg-[#6B9BD1]/10 rounded-2xl" data-id="element-280">
                <span className="text-5xl font-extrabold text-[#3A3A3A]" data-id="element-281">
                  $9.99
                </span>
              </div>

              <a href="https://43skvt-1y.myshopify.com/products/adios-rabietas-en-la-mesa-diversion-sin-pantalla-tarjetas-imprimibles" target="_blank" rel="noopener noreferrer" className="block w-full bg-[#6B9BD1] hover:bg-[#5a8ac0] text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all hover:scale-105 text-center text-lg" data-id="element-282">
                QUIERO LAS TARJETAS
              </a>
              <div className="flex flex-wrap justify-center gap-3 text-xs text-[#3A3A3A]/70 font-semibold mt-4" data-id="element-283">
                <span data-id="element-284">✔ Acceso inmediato</span>
                <span data-id="element-285">✔ Descarga instantánea</span>
                <span data-id="element-286">✔ Pago 100% seguro</span>
              </div>
            </div>

            {/* OPCIÓN 3: KIT COMPLETO (DESTACADA) */}
            <div className="bg-gradient-to-b from-white to-[#F5EAEA] rounded-3xl p-8 shadow-2xl border-4 border-[#D88C7A] relative transform md:scale-105 hover:shadow-3xl transition-shadow duration-300" data-id="element-287">
              <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-[#D88C7A] to-[#C57665] text-white px-8 py-2.5 rounded-full font-bold text-sm shadow-xl whitespace-nowrap animate-pulse" data-id="element-288">
                ⭐ RECOMENDADO
              </div>

              <div className="text-center mb-8 mt-6" data-id="element-289">
                <span className="inline-block bg-[#D88C7A] text-white px-5 py-2.5 rounded-full text-sm font-bold mb-5 shadow-md" data-id="element-290">
                  🔴 OPCIÓN 3
                </span>
                <h3 className="text-3xl font-extrabold text-[#3A3A3A] mb-2 tracking-tight" data-id="element-291">
                  KIT PREMIUM
                </h3>
                <p className="text-[#D88C7A] text-base font-bold mb-3" data-id="element-292">
                  Guía + técnicas para regular las molestias de tu hijo
                </p>
                <p className="text-[#3A3A3A]/60 text-sm italic font-semibold" data-id="element-293">
                  La transformación completa
                </p>
              </div>

              <div className="space-y-4 mb-6" data-id="element-294">
                {['Incluye la guía + bonos y tarjetas imprimibles', 'Sabes qué hacer + tienes cómo aplicarlo', 'Funciona incluso cuando estás cansada'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-295">
                    <CheckIcon className="w-5 h-5 text-[#D88C7A] flex-shrink-0 mt-0.5" data-id="element-296" />
                    <span className="text-[#3A3A3A] text-base font-bold" data-id="element-297">
                      {item}
                    </span>
                  </div>)}
              </div>

              <div className="bg-[#D88C7A]/10 p-4 rounded-xl border-2 border-[#D88C7A]/30 mb-6" data-id="element-298">
                <p className="text-[#C57665] font-extrabold text-base text-center" data-id="element-299">
                  👉 ESTA ES LA OPCIÓN MÁS ELEGIDA POR LAS MAMÁS
                </p>
              </div>

              <div className="text-center mb-8 py-4 bg-[#D88C7A]/10 rounded-2xl" data-id="element-300">
                <span className="text-5xl font-extrabold text-[#D88C7A]" data-id="element-301">
                  $15.99
                </span>
              </div>

              <a href="https://43skvt-1y.myshopify.com/products/guia-de-crianza-con-inteligencia-emocional-premium" target="_blank" rel="noopener noreferrer" className="block w-full bg-gradient-to-r from-[#D88C7A] to-[#C57665] hover:from-[#C57665] hover:to-[#b5685a] text-white font-extrabold py-5 px-6 rounded-xl shadow-xl transition-all hover:scale-105 text-center text-lg" data-id="element-302">
                QUIERO TODO EL KIT COMPLETO
              </a>
              <div className="flex flex-wrap justify-center gap-3 text-xs text-[#3A3A3A]/70 font-semibold mt-4" data-id="element-303">
                <span data-id="element-304">✔ Acceso inmediato</span>
                <span data-id="element-305">✔ Descarga instantánea</span>
                <span data-id="element-306">✔ Pago 100% seguro</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 10. TESTIMONIOS */}
      <section className="py-20 px-4 bg-[#F7F3EF] border-y border-[#E8CFCF]/30" data-id="element-307">
        <div className="max-w-4xl mx-auto" data-id="element-308">
          <div className="text-center mb-12" data-id="element-309">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] mb-4 uppercase tracking-wide" data-id="element-310">
              LO QUE OTRAS MAMÁS ESTÁN LOGRANDO
            </h2>
            <div className="flex justify-center gap-1 text-amber-400 mb-2" data-id="element-311">
              <Star className="w-6 h-6 fill-current" data-id="element-312" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-313">
            {[{
            quote: 'Yo era de las que gritaba y después me sentía fatal… esta guía me enseñó EXACTAMENTE qué decir en una rabieta. Hoy reacciono diferente y mi hijo también.',
            author: 'María G.'
          }, {
            quote: 'Lo mejor son las tarjetas… las imprimí y las usé el mismo día. No tuve que pensar, solo seguir los pasos. De verdad funciona.',
            author: 'Andrea P.'
          }, {
            quote: 'Pensé que yo era el problema como mamá… pero entendí que solo me faltaban herramientas. Ahora tengo más calma y seguridad.',
            author: 'Laura M.'
          }, {
            quote: 'Mi casa era un caos con las rabietas… hoy tengo más control sin gritar. Esto debería enseñarse a todas las mamás.',
            author: 'Daniela R.'
          }, {
            quote: 'El pack completo vale totalmente la pena. Las tarjetas son lo que más uso porque te dicen qué hacer en el momento exacto.',
            author: 'Sofía C.'
          }, {
            quote: 'No estaba segura de comprar… pero fue la mejor decisión. Es práctico, directo y realmente ayuda.',
            author: 'Valentina T.'
          }, {
            quote: 'Dejé de sentirme culpable todos los días. Ahora entiendo mejor a mi hijo y cómo actuar.',
            author: 'Camila R.'
          }, {
            quote: 'He leído mucho sobre crianza… pero esto es lo único que me dice qué hacer en la vida real.',
            author: 'Paula M.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-6 rounded-2xl shadow-sm border border-[#E8CFCF]/40 relative" data-id="element-314">
                <div className="absolute -top-3 left-6 bg-[#F5EAEA] text-[#D88C7A] p-2 rounded-lg" data-id="element-315">
                  <span className="text-xl" data-id="element-316">❝</span>
                </div>
                <p className="text-[#3A3A3A]/70 italic leading-relaxed pt-4" data-id="element-317">
                  "{item.quote}"
                </p>
                <p className="text-[#D88C7A] font-bold text-sm mt-3" data-id="element-318">
                  – {item.author}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 11. SEGURIDAD - ACCESO INMEDIATO */}
      <section className="py-16 px-4 bg-white border-b border-[#E8CFCF]/30" data-id="element-319">
        <div className="max-w-3xl mx-auto" data-id="element-320">
          <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] mb-8 text-center uppercase tracking-wide" data-id="element-321">
            ACCESO INMEDIATO
          </h2>
          <div className="space-y-4 mb-6" data-id="element-322">
            {['Descarga instantánea', 'Lo puedes usar hoy mismo', 'Material práctico y fácil de aplicar'].map((item, i) => <div key={i} className="flex items-start gap-4 p-5 rounded-xl bg-[#F5EAEA] border border-[#E8CFCF]" data-id="element-323">
                <CheckIcon className="w-6 h-6 text-[#A8B5A2] flex-shrink-0 mt-0.5" data-id="element-324" />
                <p className="text-[#3A3A3A]/80 text-lg font-semibold" data-id="element-325">
                  {item}
                </p>
              </div>)}
          </div>
          <div className="bg-[#EDF1EA] p-6 rounded-2xl border border-[#A8B5A2]/30 text-center" data-id="element-326">
            <p className="text-[#3A3A3A] font-bold text-xl" data-id="element-327">👉 Empieza ahora</p>
          </div>
        </div>
      </section>

      {/* 12. CIERRE FINAL */}
      <section className="py-20 px-4 bg-[#F7F3EF]" data-id="element-328">
        <div className="max-w-3xl mx-auto text-center" data-id="element-329">
          <h2 className="text-3xl md:text-5xl font-bold text-[#3A3A3A] mb-8 leading-tight" data-id="element-330">
            Puedes seguir intentando sola…
          </h2>
          <p className="text-xl md:text-2xl text-[#3A3A3A]/80 leading-relaxed mb-8" data-id="element-331">
            O puedes empezar hoy a tener herramientas reales para manejar las
            emociones de tu hijo sin gritos ni culpa
          </p>
          <p className="text-2xl md:text-4xl font-bold text-[#D88C7A] leading-tight mb-8" data-id="element-332">
            Tu hijo no necesita una mamá perfecta… necesita una mamá que sepa
            qué hacer en momentos difíciles
          </p>
          <p className="text-lg text-[#D88C7A] font-bold mb-10" data-id="element-333">
            👉 Este puede ser el cambio que estás buscando
          </p>
          <a href="https://43skvt-1y.myshopify.com/collections/frontpage" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold text-xl py-4 px-10 rounded-xl shadow-lg transition-all transform hover:scale-105 text-center" data-id="element-334">
            QUIERO EMPEZAR HOY
          </a>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-[#3A3A3A]/70 font-semibold mt-4" data-id="element-335">
            <span data-id="element-336">✔ Acceso inmediato</span>
            <span data-id="element-337">✔ Descarga instantánea</span>
            <span data-id="element-338">✔ Pago 100% seguro</span>
          </div>
        </div>
      </section>

      <Footer data-id="element-339" />
    </div>;
}